import type { Dict } from './uk';

export const en: Dict = {
  'app.skip': 'Skip to content',
  'app.loading': 'Loading…',
  'nav.cases': 'My cases',
  'nav.privacy': 'My data',
  'nav.main': 'Main navigation',
  'lang.label': 'Language',
  'theme.label': 'Appearance',
  'theme.system': 'System',
  'theme.light': 'Light',
  'theme.dark': 'Dark',

  'common.retry': 'Try again',
  'common.back': 'Back',
  'common.cabinet': 'Personal account',

  'login.title': 'Sign in',
  'login.intro': 'Here you can see how your case is progressing, send us documents and write to your manager. No password needed — we will send you a one-time code.',
  'login.method': 'How to send the code',
  'login.byPhone': 'SMS to phone',
  'login.byEmail': 'Email',
  'login.phoneLabel': 'Phone number',
  'login.emailLabel': 'Email address',
  'login.destHint': 'The one you gave to our manager.',
  'login.phonePlaceholder': '+380 67 123 45 67',
  'login.emailPlaceholder': 'name@example.com',
  'login.submit': 'Get a code',
  'login.sending': 'Sending…',
  'login.codeTitle': 'Enter the code',
  'login.codeSentPhone': 'We sent a text message to {dest}.',
  'login.codeSentEmail': 'We sent an email to {dest}.',
  'login.codeNote': 'If we have these contact details, the code will arrive within a minute. It is valid for 10 minutes.',
  'login.codeLabel': '6-digit code',
  'login.verify': 'Sign in',
  'login.verifying': 'Checking…',
  'login.resend': 'Send the code again',
  'login.resendIn': 'You can request a new code in {time}',
  'login.resent': 'A new code has been sent.',
  'login.change': 'Change phone or email',
  'login.help': 'No code? Call us — we will help.',
  'login.empty': 'Please enter your phone number or email.',

  'err.invalid_destination': 'There seems to be a mistake in the number or address. Please check it.',
  'err.invalid_code': 'The code did not match or has expired. Check the digits or ask for a new code.',
  'err.rate_limited': 'Too many attempts. Please wait a few minutes and try again.',
  'err.rate_limited_wait': 'Too many attempts. You can try again in {time}.',
  'err.network': 'No connection. Check your internet and try again.',
  'err.unauthorized': 'Your session has ended. Please sign in again.',
  'err.forbidden': 'You do not have access to this page.',
  'err.not_found': 'We could not find this page or document.',
  'err.validation': 'Please check what you entered.',
  'err.generic': 'Something went wrong. Please try again or write to us in the chat.',
  'err.code_incomplete': 'Please enter all 6 digits of the code.',

  'cases.title': 'My cases',
  'cases.empty': 'There are no cases here yet. If you have just contacted us, your case will appear after you speak with a manager.',
  'cases.number': 'Case No. {n}',
  'cases.updated': 'Updated {date}',
  'cases.now': 'Now',
  'cases.open': 'Open case',

  'case.sections': 'Page sections',
  'case.stages': 'Stages',
  'case.stage.done': 'Done',
  'case.stage.current': 'Now',
  'case.stage.next': 'Next',
  'case.needs': 'What we need from you',
  'case.needsIntro': 'Take a photo of the document with your phone or choose a file. The text just needs to be clearly readable.',
  'case.needsDone': 'Thank you. Nothing is needed from you right now.',
  'case.needsCount': 'Remaining: {n}',
  'case.rejectedReason': 'What is wrong: {reason}',
  'case.rejectedNoReason': 'Your manager will explain in the chat what needs to be fixed.',
  'case.replaceHint': 'Sent the wrong file? You can send another one until we have checked it.',

  'status.missing': 'Needed',
  'status.optional': 'Optional',
  'status.uploaded': 'Being checked',
  'status.accepted': 'Accepted',
  'status.rejected': 'Please redo',

  'upload.takePhoto': 'Take a photo',
  'upload.chooseFile': 'Choose a file',
  'upload.replace': 'Send a different file',
  'upload.limits': 'Photo or PDF, up to {mb} MB.',
  'upload.preparing': 'Preparing…',
  'upload.uploading': 'Uploading: {pct}%',
  'upload.finishing': 'Saving…',
  'upload.done': 'File received. We will check it shortly.',
  'upload.tooLarge': 'The file is too large ({size}). The limit is {mb} MB. Try taking the photo again or making the PDF smaller.',
  'upload.badType': 'This file type is not supported. Photos (JPG, PNG, HEIC) or PDF are fine.',
  'upload.empty': 'The file is empty. Please choose it again.',
  'upload.failed': 'The file could not be uploaded.',
  'upload.retry': 'Try again',
  'upload.cancel': 'Cancel',
  'upload.progress': 'Upload progress',

  'docs.fromUs': 'Documents from us',
  'docs.empty': 'Documents we prepare for you will appear here.',
  'docs.download': 'Download',
  'docs.opening': 'Opening…',
  'docs.yours': 'Sent by you',

  'chat.title': 'Chat with your manager',
  'chat.intro': 'Write whenever it suits you. Your manager will reply as soon as possible.',
  'chat.urgentCall': 'For urgent matters it is better to call:',
  'chat.empty': 'No messages yet. Feel free to ask anything.',
  'chat.label': 'Your message',
  'chat.placeholder': 'Write a message…',
  'chat.send': 'Send',
  'chat.sending': 'Sending…',
  'chat.failed': 'Not sent.',
  'chat.retry': 'Send again',
  'chat.you': 'You',
  'chat.manager': 'Manager',
  'chat.offline': 'No connection. New messages will appear once the connection is back.',

  'timeline.title': 'History',
  'timeline.empty': 'No events yet.',
  'event.case_created': 'Case opened',
  'event.state_changed': 'New stage: {state}',
  'event.state_changed_plain': 'The case has moved to a new stage',
  'event.document_uploaded': 'Document received: {doc}',
  'event.document_accepted': 'Document accepted: {doc}',
  'event.documents_accepted': 'Documents accepted: {docs}',
  'event.documents_uploaded': 'Documents received: {docs}',
  'event.document_rejected': 'Document needs to be redone: {doc}',
  'event.document_ready': 'Document prepared: {doc}',
  'event.document_plain': 'Documents updated',
  'event.message': 'New message from your manager',
  'event.generic': 'Case update',
  'event.order_shipped': 'Order shipped',
  'event.order_completed': 'Order completed',

  'privacy.title': 'My data',
  'privacy.intro': 'We keep only what is needed for your case and do not share it without need.',
  'privacy.exportTitle': 'A copy of your data',
  'privacy.exportHint': 'A file with everything we store about you (under GDPR).',
  'privacy.export': 'Download my data',
  'privacy.exporting': 'Preparing the file…',
  'privacy.exported': 'The file has been saved to your device.',
  'privacy.erase': 'If you would like us to delete your data, write to us. We will reply within 30 days.',
  'privacy.contacts': 'Contacts',
  'privacy.phone': 'Phone',
  'privacy.duty': '24/7',
  'privacy.email': 'Email',
  'privacy.dpo': 'Data protection questions',
  'privacy.company': 'Company',
  'privacy.address': 'Address',
  'privacy.session': 'Sign out',
  'privacy.logoutHint': 'If you are using someone else’s phone or computer, sign out when you are done.',
  'privacy.logout': 'Sign out',
  'privacy.loggingOut': 'Signing out…',

  // --- phase 5: steps, notifications, feedback, case site
  'nav.notifications': 'Notifications',
  'notif.title': 'Notifications',
  'notif.intro': 'All messages about your cases are kept here, even if an e-mail or SMS did not arrive.',
  'notif.empty': 'There are no notifications yet.',
  'notif.via.email': 'Sent by e-mail',
  'notif.via.sms': 'Sent by SMS',
  'notif.via.telegram': 'Sent via Telegram',
  'notif.via.viber': 'Sent via Viber',
  'notif.via.whatsapp': 'Sent via WhatsApp',
  'notif.logged': 'Saved in your account',
  'notif.queued': 'We will send it in the morning',
  'notif.new': 'New',
  'notif.unread': 'New notifications: {n}',
  'notif.more': 'Show earlier',
  'notif.today': 'Today',
  'notif.yesterday': 'Yesterday',
  'step.accepted': 'Case accepted',
  'step.docs_collecting': 'Collecting documents',
  'step.docs_ready': 'Documents ready',
  'step.picked_up': 'Taken into our care',
  'step.in_transit': 'On the way',
  'step.border_crossed': 'Border crossed',
  'step.delivered': 'Delivered',
  'step.burial': 'Burial',
  'step.hint.accepted': 'We have accepted the case and are already working on it.',
  'step.hint.docs_collecting': 'We are collecting the necessary documents. If we need anything from you, we will write.',
  'step.hint.docs_ready': 'The documents are ready. We are preparing the next step.',
  'step.hint.picked_up': 'We will take care of everything that is needed.',
  'step.hint.in_transit': 'We will let you know when the border is crossed and when we arrive.',
  'step.hint.in_transit_local': 'We will let you know when we arrive.',
  'step.hint.border_crossed': 'The border is behind us. We will let you know when we arrive.',
  'step.hint.delivered': 'We have arrived. Your manager will contact you about the next steps.',
  'step.hint.burial': 'We are here if you need any help. You can write to your manager in the chat.',
  'event.step': 'New step: {step}',
  'feedback.title': 'How did we do?',
  'feedback.intro': 'A few words from you will help us to be more attentive to other families.',
  'feedback.rating': 'Rating from 1 to 5',
  'feedback.rate': '{n} of 5',
  'feedback.scale': '1 — poor, 5 — very good',
  'feedback.text': 'What could we have done better? (optional)',
  'feedback.optOut': 'Do not send me such requests again',
  'feedback.submit': 'Send the answer',
  'feedback.sending': 'Sending…',
  'feedback.pickRating': 'Please choose a rating.',
  'feedback.thanks': 'Thank you. Your answer matters to us.',
  'feedback.expired': 'The time to answer has passed. You can always write to your manager in your account.',
  'feedback.optedOut': 'You have declined such requests, so we will not send them again.',
  'feedback.optedOutDone': 'We will not send you such requests again.',
  'feedback.notFound': 'This link is not valid. If you would like to share your impressions, write to your manager in your account.',
  'feedback.card': 'Feedback',
  'feedback.cardIntro': 'If you have a minute, tell us how we did.',
  'feedback.toCabinet': 'Go to your account',
  'privacy.feedbackTitle': 'Feedback requests',
  'privacy.feedbackHint': 'After a case is completed, we may ask once how we did.',
  'privacy.feedbackOptOut': 'Do not send me feedback requests',
  'privacy.saved': 'Saved.',
  'site.contacts': 'Contact us',
};
